import { useEffect, useMemo } from "react";

import {
  currentLocaleAtom,
  elementsSelectionIdState,
  KpkMedia,
  tabLoadingAtom,
  useLoadableErrorLogger,
} from "@keepeek/commons";
import { useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { getCustomerConfig } from "../../../lib/config/customer-config-utils";
import { DownloadManagerAttachmentsByElement } from "../atoms/attachments";
import { attachmentsByElementsIdQuerySelector } from "../selectors/attachments";

const customer = getCustomerConfig();

export type UseAttachmentsDownloadManager = {
  attachmentsByElements: DownloadManagerAttachmentsByElement[];
  elementIds: KpkMedia["id"][] | null;
  error: boolean;
  errorMessage: string;
};

export const useAttachmentsDownloadManager = (): UseAttachmentsDownloadManager => {
  // SETUP
  const locale = useRecoilValue(currentLocaleAtom);

  const elementIds = useRecoilValue(elementsSelectionIdState);
  const query = useRecoilValueLoadable(
    attachmentsByElementsIdQuerySelector(locale || customer.customerConfig.dataLocale || ""),
  );
  useLoadableErrorLogger(query, "attachmentsByElementsIdQuerySelector query");

  const attachmentsByElements = useMemo(
    () => (query.state === "hasValue" && query.contents ? query.contents : []),
    [query.contents, query.state],
  );

  const error = query.state === "hasError";
  const errorMessage = query.state === "hasError" && query.contents ? query.contents : "";

  const setTabLoading = useSetRecoilState(tabLoadingAtom);
  useEffect(() => {
    setTabLoading(query.state === "loading");
  }, [query, setTabLoading]);

  return {
    error,
    errorMessage,
    attachmentsByElements,
    elementIds,
  };
};
